import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface Agent {
    id: string;
    name: string;
    description: string;
    icon: string;
    enabled: boolean;
    document_count: number;
    allow_multiple: boolean;
}

interface CompareResult {
    id?: number;
    comparison_id?: number;
    agent_type?: string;
    agreement_title?: string;
    executive_summary?: {
        overall_assessment?: string;
        major_risks?: string[];
        major_discrepancies?: string[];
        final_recommendation?: string;
    };
    dashboard?: {
        match_score?: number;
        risk_score?: number;
        match_confidence?: number;
        risk_level?: string;
        overall_status?: string;
        statistics?: {
            total_issues?: number;
            total_findings?: number;
            critical_issues?: number;
            critical_findings?: number;
            high_issues?: number;
            high_findings?: number;
            medium_issues?: number;
            medium_findings?: number;
            low_issues?: number;
            low_findings?: number;
        };
    };
    overall_summary?: string; // Legacy
    summary?: {
        overall_status?: string;
        match_confidence?: number;
        key_risk_notes?: string;
    }; // Legacy
    issues?: any[];
    findings?: any[];
    missing_elements?: any[];
    inconsistencies?: any[];
    created_at?: string;
    status?: string;
    error_message?: string;
}

interface AICompareModalProps {
    isOpen: boolean;
    onClose: () => void;
    taskId: number;
    darkMode: boolean;
}

const AICompareModal: React.FC<AICompareModalProps> = ({
    isOpen,
    onClose,
    taskId,
    darkMode
}) => {
    // State
    const [step, setStep] = useState<1 | 2>(1);
    const [agents, setAgents] = useState<Agent[]>([]);
    const [selectedAgent, setSelectedAgent] = useState<string>('');
    const [pdfFiles1, setPdfFiles1] = useState<File[]>([]);
    const [pdfFiles2, setPdfFiles2] = useState<File[]>([]);
    const [isComparing, setIsComparing] = useState(false);
    const [isLoadingCache, setIsLoadingCache] = useState(false);
    const [compareResult, setCompareResult] = useState<CompareResult | null>(null);
    const [loadingAgents, setLoadingAgents] = useState(true);

    // Get selected agent config
    const selectedAgentConfig = agents.find(a => a.id === selectedAgent);
    const documentCount = selectedAgentConfig?.document_count || 2;
    const allowMultiple = selectedAgentConfig?.allow_multiple ?? true;

    // Load agents on mount
    useEffect(() => {
        if (isOpen) {
            loadAgents();
        }
    }, [isOpen]);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setStep(1);
            setSelectedAgent('');
            setPdfFiles1([]);
            setPdfFiles2([]);
            setCompareResult(null);
        }
    }, [isOpen]);

    // Load cached result when agent is selected
    useEffect(() => {
        if (selectedAgent && taskId) {
            loadCachedResult();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAgent, taskId]);

    const loadAgents = async () => {
        setLoadingAgents(true);
        try {
            const agentsList = await apiService.getAIAgents();
            setAgents(agentsList);
        } catch (err) {
            console.error('Error loading agents:', err);
            // Fallback to default agents if API fails
            setAgents([
                { id: 'agent_1', name: 'Contract Comparison', description: 'Membandingkan 2 dokumen', icon: 'fa-code-compare', enabled: true, document_count: 2, allow_multiple: true },
                { id: 'agent_2', name: 'Legal Issue Detector', description: 'Menganalisis dokumen', icon: 'fa-magnifying-glass-chart', enabled: true, document_count: 1, allow_multiple: true },
                { id: 'agent_3', name: 'Document Reconciliation', description: 'Verifikasi kesesuaian', icon: 'fa-file-circle-check', enabled: true, document_count: 2, allow_multiple: true },
            ]);
        } finally {
            setLoadingAgents(false);
        }
    };

    const loadCachedResult = async () => {
        if (!selectedAgent || !taskId) return;

        setIsLoadingCache(true);
        try {
            const cached = await apiService.getTaskAIComparisonByAgent(taskId, selectedAgent);
            if (cached) {
                // If the cached result is still in progress, start polling
                if (cached.status === 'PENDING' || cached.status === 'PROCESSING') {
                    setCompareResult(null); // Don't show incomplete result
                    setIsComparing(true);
                    pollComparisonStatus(cached.id || cached.comparison_id);
                } else {
                    setCompareResult(cached);
                }
            } else {
                setCompareResult(null);
            }
        } catch (err) {
            console.log('No cached comparison found');
            setCompareResult(null);
        } finally {
            setIsLoadingCache(false);
        }
    };

    const handleAgentSelect = (agentId: string) => {
        setSelectedAgent(agentId);
        setCompareResult(null);
        setPdfFiles1([]);
        setPdfFiles2([]);
        setStep(2);
    };

    const handleFileChange = (files: FileList | null, group: 1 | 2) => {
        if (!files) return;

        const validFiles: File[] = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (file.name.toLowerCase().endsWith('.pdf')) {
                validFiles.push(file);
            } else {
                alert(`File ${file.name} bukan PDF dan akan diabaikan`);
            }
        }

        if (group === 1) {
            if (allowMultiple) {
                setPdfFiles1(prev => [...prev, ...validFiles]);
            } else {
                setPdfFiles1(validFiles.slice(0, 1));
            }
        } else {
            if (allowMultiple) {
                setPdfFiles2(prev => [...prev, ...validFiles]);
            } else {
                setPdfFiles2(validFiles.slice(0, 1));
            }
        }
    };

    const removeFile = (index: number, group: 1 | 2) => {
        if (group === 1) {
            setPdfFiles1(prev => prev.filter((_, i) => i !== index));
        } else {
            setPdfFiles2(prev => prev.filter((_, i) => i !== index));
        }
    };

    const pollComparisonStatus = async (comparisonId: number) => {
        try {
            const statusResult = await apiService.getAIAnalysisStatus(comparisonId);
            console.log('Polling status:', statusResult.status);

            if (statusResult.status === 'COMPLETED') {
                setCompareResult(statusResult);
                setIsComparing(false);
            } else if (statusResult.status === 'FAILED') {
                setIsComparing(false);
                alert('Analisis gagal: ' + (statusResult.error_message || 'Unknown error'));
            } else {
                // Continue polling
                setTimeout(() => pollComparisonStatus(comparisonId), 3000);
            }
        } catch (err) {
            console.error('Error polling status:', err);
            // Don't stop polling immediately on network error, maybe retry a few times? 
            // For now, just retry
            setTimeout(() => pollComparisonStatus(comparisonId), 5000);
        }
    };

    const handleCompare = async () => {
        // Validate based on document count
        if (pdfFiles1.length === 0) {
            alert('Silakan pilih minimal 1 file PDF untuk dianalisis');
            return;
        }
        if (documentCount === 2 && pdfFiles2.length === 0) {
            alert('Silakan pilih file untuk kedua grup dokumen');
            return;
        }

        setIsComparing(true);
        setCompareResult(null);

        try {
            const result = await apiService.analyzeWithAI(pdfFiles1, documentCount === 2 ? pdfFiles2 : null, taskId, selectedAgent);

            // Start polling if we got a comparison_id and status is PENDING
            if (result.comparison_id && result.status === 'PENDING') {
                pollComparisonStatus(result.comparison_id);
            } else {
                // Fallback for immediate response (if any) or error
                setCompareResult(result);
                setIsComparing(false);
            }
        } catch (err: any) {
            console.error('AI Analysis Error:', err);
            alert('Gagal memulai analisis: ' + err.message);
            setIsComparing(false);
        }
    };

    const handleDeleteResult = async () => {
        if (!compareResult?.id && !compareResult?.comparison_id) return;

        const comparisonId = compareResult.id || compareResult.comparison_id;
        if (!window.confirm('Hapus hasil analisis ini? Anda dapat menjalankan analisis ulang setelahnya.')) return;

        try {
            await apiService.deleteTaskAIComparison(comparisonId!);
            setCompareResult(null);
            setPdfFiles1([]);
            setPdfFiles2([]);
        } catch (err: any) {
            console.error('Error deleting comparison:', err);
            alert('Gagal menghapus hasil analisis');
        }
    };

    const handleBack = () => {
        if (step === 2) {
            setStep(1);
            setSelectedAgent('');
            setCompareResult(null);
            setPdfFiles1([]);
            setPdfFiles2([]);
        }
    };

    // Get document group labels based on agent type
    const getDocumentLabels = () => {
        if (selectedAgent === 'agent_3') {
            return {
                group1: 'Lampiran Agreement',
                group2: 'Dokumen Pendukung (Invoice, PO, dll)'
            };
        }
        return {
            group1: documentCount === 1 ? 'Dokumen PDF' : 'Dokumen A',
            group2: 'Dokumen B'
        };
    };

    const labels = getDocumentLabels();

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* Backdrop */}
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className={`absolute inset-0 ${darkMode ? 'bg-gray-900' : 'bg-gray-500'} opacity-75`}></div>
                </div>

                {/* Modal */}
                <div className={`inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    {/* Header */}
                    <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                {step === 2 && (
                                    <button
                                        onClick={handleBack}
                                        className={`mr-3 p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
                                    >
                                        <i className="fa-solid fa-arrow-left"></i>
                                    </button>
                                )}
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${darkMode ? 'bg-indigo-900/30' : 'bg-indigo-100'}`}>
                                    <i className={`fa-solid fa-robot ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}></i>
                                </div>
                                <div>
                                    <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                        AI Document Analysis
                                    </h3>
                                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {step === 1 ? 'Pilih agent untuk analisis' : `Agent: ${selectedAgentConfig?.name}`}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                            >
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>

                        {/* Step Indicator */}
                        <div className="flex items-center mt-4">
                            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${step >= 1 ? 'bg-indigo-600 text-white' : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                                1
                            </div>
                            <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-indigo-600' : darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${step >= 2 ? 'bg-indigo-600 text-white' : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                                2
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">
                        {/* Step 1: Agent Selection */}
                        {step === 1 && (
                            <div>
                                <h4 className={`text-sm font-medium mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Pilih Agent AI untuk Analisis:
                                </h4>

                                {loadingAgents ? (
                                    <div className="flex items-center justify-center py-12">
                                        <i className="fa-solid fa-spinner fa-spin text-2xl text-indigo-600"></i>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {agents.map((agent) => (
                                            <button
                                                key={agent.id}
                                                onClick={() => handleAgentSelect(agent.id)}
                                                disabled={!agent.enabled}
                                                className={`p-6 rounded-xl border-2 text-left transition-all ${!agent.enabled
                                                    ? darkMode
                                                        ? 'bg-gray-800 border-gray-700 opacity-50 cursor-not-allowed'
                                                        : 'bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed'
                                                    : darkMode
                                                        ? 'bg-gray-700/50 border-gray-600 hover:border-indigo-500 hover:bg-gray-700'
                                                        : 'bg-white border-gray-200 hover:border-indigo-500 hover:shadow-md'
                                                    }`}
                                            >
                                                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${darkMode ? 'bg-indigo-900/30' : 'bg-indigo-100'
                                                    }`}>
                                                    <i className={`fa-solid ${agent.icon} text-xl ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}></i>
                                                </div>
                                                <h5 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                                    {agent.name}
                                                </h5>
                                                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    {agent.description}
                                                </p>
                                                {/* Document count badge */}
                                                <div className={`mt-3 inline-flex items-center px-2 py-1 rounded text-xs ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                                    <i className="fa-solid fa-file-pdf mr-1"></i>
                                                    {agent.document_count} grup dokumen
                                                    {agent.allow_multiple && <span className="ml-1">(multi-file)</span>}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Step 2: Upload & Analyze */}
                        {step === 2 && (
                            <div>
                                {/* Loading cached result */}
                                {isLoadingCache && (
                                    <div className="flex items-center justify-center py-8">
                                        <i className="fa-solid fa-spinner fa-spin text-2xl text-indigo-600 mr-3"></i>
                                        <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Memuat hasil analisis...</span>
                                    </div>
                                )}

                                {/* Show cached result if exists */}
                                {!isLoadingCache && compareResult && (
                                    <div>
                                        <div className={`flex items-center justify-between mb-4 p-4 rounded-lg ${darkMode ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'}`}>
                                            <div className="flex items-center">
                                                <i className={`fa-solid fa-check-circle mr-3 ${darkMode ? 'text-green-400' : 'text-green-600'}`}></i>
                                                <div>
                                                    <p className={`font-medium ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
                                                        Hasil Analisis Tersedia
                                                    </p>
                                                    {compareResult.created_at && (
                                                        <p className={`text-sm ${darkMode ? 'text-green-400/70' : 'text-green-600'}`}>
                                                            Dibuat: {new Date(compareResult.created_at).toLocaleString('id-ID')}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={handleDeleteResult}
                                                className={`px-3 py-2 text-sm rounded-lg transition-colors ${darkMode ? 'bg-red-900/50 text-red-300 hover:bg-red-900' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                                            >
                                                <i className="fa-solid fa-trash mr-2"></i>
                                                Hapus & Jalankan Ulang
                                            </button>
                                        </div>

                                        {/* Results Display */}
                                        <AgentResultViewer result={compareResult} darkMode={darkMode} agentType={selectedAgent} />
                                    </div>
                                )}

                                {/* Upload form if no cached result */}
                                {!isLoadingCache && !compareResult && !isComparing && (
                                    <div>
                                        {/* Document upload grid - adapts based on document_count */}
                                        <div className={`grid gap-6 mb-6 ${documentCount === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                                            {/* Document Group 1 */}
                                            <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                                                <h4 className={`font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                    {labels.group1}
                                                    {allowMultiple && <span className="text-xs font-normal ml-2">(dapat upload banyak file)</span>}
                                                </h4>
                                                <input
                                                    type="file"
                                                    accept=".pdf"
                                                    multiple={allowMultiple}
                                                    onChange={(e) => handleFileChange(e.target.files, 1)}
                                                    className={`block w-full text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'} file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold ${darkMode ? 'file:bg-indigo-900 file:text-indigo-200' : 'file:bg-indigo-50 file:text-indigo-700'}`}
                                                />
                                                {/* File list */}
                                                {pdfFiles1.length > 0 && (
                                                    <div className="mt-3 space-y-2">
                                                        {pdfFiles1.map((file, idx) => (
                                                            <div key={idx} className="flex items-center justify-between text-sm">
                                                                <div className="flex items-center">
                                                                    <i className="fa-solid fa-file-pdf mr-2 text-red-500"></i>
                                                                    <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>{file.name}</span>
                                                                </div>
                                                                <button
                                                                    onClick={() => removeFile(idx, 1)}
                                                                    className={`p-1 rounded hover:bg-red-100 ${darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500'}`}
                                                                >
                                                                    <i className="fa-solid fa-times"></i>
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Document Group 2 - only show if document_count is 2 */}
                                            {documentCount === 2 && (
                                                <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                                                    <h4 className={`font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                        {labels.group2}
                                                        {allowMultiple && <span className="text-xs font-normal ml-2">(dapat upload banyak file)</span>}
                                                    </h4>
                                                    <input
                                                        type="file"
                                                        accept=".pdf"
                                                        multiple={allowMultiple}
                                                        onChange={(e) => handleFileChange(e.target.files, 2)}
                                                        className={`block w-full text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'} file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold ${darkMode ? 'file:bg-indigo-900 file:text-indigo-200' : 'file:bg-indigo-50 file:text-indigo-700'}`}
                                                    />
                                                    {/* File list */}
                                                    {pdfFiles2.length > 0 && (
                                                        <div className="mt-3 space-y-2">
                                                            {pdfFiles2.map((file, idx) => (
                                                                <div key={idx} className="flex items-center justify-between text-sm">
                                                                    <div className="flex items-center">
                                                                        <i className="fa-solid fa-file-pdf mr-2 text-red-500"></i>
                                                                        <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>{file.name}</span>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => removeFile(idx, 2)}
                                                                        className={`p-1 rounded hover:bg-red-100 ${darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500'}`}
                                                                    >
                                                                        <i className="fa-solid fa-times"></i>
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Analyze Button */}
                                        <button
                                            onClick={handleCompare}
                                            disabled={isComparing || pdfFiles1.length === 0 || (documentCount === 2 && pdfFiles2.length === 0)}
                                            className={`w-full py-3 rounded-lg font-semibold transition-colors ${isComparing || pdfFiles1.length === 0 || (documentCount === 2 && pdfFiles2.length === 0)
                                                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                                : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                                }`}
                                        >
                                            <i className="fa-solid fa-magnifying-glass mr-2"></i>
                                            {documentCount === 1 ? 'Analisis Dokumen' : 'Analisis & Bandingkan'}
                                        </button>
                                    </div>
                                )}

                                {/* Loading State - Show when analysis is in progress */}
                                {!isLoadingCache && !compareResult && isComparing && (
                                    <div className={`p-8 rounded-lg text-center ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100'}`}>
                                        {/* Animated Robot Icon */}
                                        <div className="relative inline-block mb-6">
                                            <div className={`w-24 h-24 rounded-full flex items-center justify-center ${darkMode ? 'bg-indigo-900/50' : 'bg-indigo-100'} animate-pulse`}>
                                                <i className={`fa-solid fa-robot text-4xl ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}></i>
                                            </div>
                                            {/* Orbiting dots animation */}
                                            <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
                                                <div className={`absolute top-0 left-1/2 w-3 h-3 -ml-1.5 rounded-full ${darkMode ? 'bg-indigo-400' : 'bg-indigo-500'}`}></div>
                                            </div>
                                            <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s', animationDelay: '1s' }}>
                                                <div className={`absolute top-0 left-1/2 w-2 h-2 -ml-1 rounded-full ${darkMode ? 'bg-purple-400' : 'bg-purple-500'}`}></div>
                                            </div>
                                            <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s', animationDelay: '2s' }}>
                                                <div className={`absolute top-0 left-1/2 w-2 h-2 -ml-1 rounded-full ${darkMode ? 'bg-pink-400' : 'bg-pink-500'}`}></div>
                                            </div>
                                        </div>

                                        {/* Status Text */}
                                        <h4 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                            <i className="fa-solid fa-clock mr-2"></i>
                                            Analisis Sedang Berjalan
                                        </h4>
                                        <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                            Dokumen Anda telah masuk antrian dan sedang diproses oleh AI
                                        </p>

                                        {/* Progress Indicator */}
                                        <div className={`max-w-md mx-auto rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white shadow-sm'}`}>
                                            <div className="flex items-center justify-between text-sm mb-2">
                                                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Status:</span>
                                                <span className={`font-medium ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                                                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                                                    Memproses...
                                                </span>
                                            </div>

                                            {/* Animated progress bar */}
                                            <div className={`h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                                <div
                                                    className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 rounded-full animate-pulse"
                                                    style={{
                                                        width: '100%',
                                                        backgroundSize: '200% 100%',
                                                        animation: 'shimmer 2s infinite linear'
                                                    }}
                                                ></div>
                                            </div>

                                            <p className={`text-xs mt-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                                <i className="fa-solid fa-info-circle mr-1"></i>
                                                Proses ini mungkin memakan waktu beberapa menit tergantung ukuran dokumen. Anda dapat menunggu atau menutup modal ini - hasil akan tersimpan otomatis.
                                            </p>
                                        </div>

                                        {/* File info being processed */}
                                        <div className={`mt-4 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                            <span>Dokumen: </span>
                                            <span className="font-medium">{pdfFiles1.map(f => f.name).join(', ')}</span>
                                            {pdfFiles2.length > 0 && (
                                                <>
                                                    <span> vs </span>
                                                    <span className="font-medium">{pdfFiles2.map(f => f.name).join(', ')}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className={`px-6 py-4 border-t ${darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                        <div className="flex justify-end">
                            <button
                                onClick={onClose}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                            >
                                Tutup
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AgentResultViewer: React.FC<{ result: CompareResult, darkMode: boolean, agentType: string }> = ({ result, darkMode, agentType }) => {
    const [activeTab, setActiveTab] = useState<'summary' | 'dashboard' | 'findings'>('summary');
    
    // Fallbacks
    const issues = result.issues || result.findings || [];
    const execSummary = result.executive_summary;
    const dashboard = result.dashboard;
    
    // Helper colors
    const getSeverityColor = (severity: string) => {
        switch (severity?.toLowerCase()) {
            case 'critical': return darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-800';
            case 'high': return darkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-800';
            case 'medium': return darkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
            case 'low': return darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800';
            default: return darkMode ? 'bg-gray-700/50 text-gray-400' : 'bg-gray-100 text-gray-600';
        }
    };

    return (
        <div className={`rounded-xl border overflow-hidden shadow-sm ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
            {/* Header */}
            <div className={`p-5 border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <h3 className={`text-lg font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {result.agreement_title || 'Analysis Result'}
                </h3>
                {(result.overall_summary || (execSummary && execSummary.overall_assessment)) && (
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {result.overall_summary || execSummary?.overall_assessment}
                    </p>
                )}
            </div>

            {/* Tabs */}
            <div className={`flex items-center space-x-1 border-b px-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                {(['summary', 'dashboard', 'findings'] as const).map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-3 text-sm font-semibold capitalize transition-colors border-b-2 -mb-px ${
                            activeTab === tab
                                ? `border-indigo-500 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`
                                : `border-transparent ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
                        }`}
                    >
                        {tab === 'summary' ? 'Executive Summary' : tab === 'dashboard' ? 'Dashboard & Stats' : `Findings (${issues.length})`}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="p-5 max-h-[500px] overflow-y-auto">
                
                {/* TAB: SUMMARY */}
                {activeTab === 'summary' && (
                    <div className="space-y-4">
                        {/* If using old format (legacy fallback) */}
                        {!execSummary && (
                            <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {result.overall_summary || 'No summary available.'}
                                </p>
                            </div>
                        )}
                        
                        {/* New Executive Summary */}
                        {execSummary && (
                            <>
                                {execSummary.overall_assessment && (
                                    <div className={`p-4 rounded-xl border-l-4 border-indigo-500 ${darkMode ? 'bg-indigo-900/10' : 'bg-indigo-50'}`}>
                                        <h4 className={`text-sm font-bold mb-2 ${darkMode ? 'text-indigo-300' : 'text-indigo-800'}`}>Overall Assessment</h4>
                                        <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{execSummary.overall_assessment}</p>
                                    </div>
                                )}
                                
                                {(execSummary.major_risks || execSummary.major_discrepancies) && (
                                    <div className={`p-4 rounded-xl border-l-4 border-red-500 ${darkMode ? 'bg-red-900/10' : 'bg-red-50'}`}>
                                        <h4 className={`text-sm font-bold mb-3 ${darkMode ? 'text-red-300' : 'text-red-800'}`}>
                                            {execSummary.major_risks ? 'Major Risks' : 'Major Discrepancies'}
                                        </h4>
                                        <ul className="space-y-2">
                                            {(execSummary.major_risks || execSummary.major_discrepancies || []).map((item, i) => (
                                                <li key={i} className={`flex items-start text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                                    <i className="fa-solid fa-triangle-exclamation text-red-500 mt-0.5 mr-2 flex-shrink-0 text-xs"></i>
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {execSummary.final_recommendation && (
                                    <div className={`p-4 rounded-xl border-l-4 border-green-500 ${darkMode ? 'bg-green-900/10' : 'bg-green-50'}`}>
                                        <h4 className={`text-sm font-bold mb-2 ${darkMode ? 'text-green-300' : 'text-green-800'}`}>Final Recommendation</h4>
                                        <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{execSummary.final_recommendation}</p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* TAB: DASHBOARD */}
                {activeTab === 'dashboard' && (
                    <div className="space-y-6">
                        {/* Legacy Summary Data for Agent 3 */}
                        {result.summary && !dashboard && (
                            <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <p className={`text-xs uppercase font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Status</p>
                                        <p className={`text-lg font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{result.summary.overall_status}</p>
                                    </div>
                                    <div>
                                        <p className={`text-xs uppercase font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Match Confidence</p>
                                        <div className="flex items-center mt-1">
                                            <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2 dark:bg-gray-700">
                                                <div className="bg-indigo-600 h-2 rounded-full" style={{ width: `${result.summary.match_confidence || 0}%` }}></div>
                                            </div>
                                            <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{result.summary.match_confidence}%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* New Dashboard Statistics */}
                        {dashboard && (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Main Score Card */}
                                    <div className={`p-5 rounded-xl border flex flex-col items-center justify-center ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                                        <h4 className={`text-sm font-semibold mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {dashboard.match_score !== undefined ? 'Match Score' : dashboard.risk_score !== undefined ? 'Risk Score' : 'Confidence'}
                                        </h4>
                                        <div className="relative w-32 h-32 flex items-center justify-center rounded-full border-8 border-indigo-100 dark:border-indigo-900/30">
                                            <svg className="absolute inset-0 w-full h-full transform -rotate-90">
                                                <circle cx="60" cy="60" r="54" fill="transparent" stroke="currentColor" strokeWidth="8"
                                                    className="text-indigo-500" strokeDasharray="339.292"
                                                    strokeDashoffset={339.292 - (339.292 * ((dashboard.match_score || dashboard.risk_score || dashboard.match_confidence || 0) / 100))} />
                                            </svg>
                                            <span className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                                {dashboard.match_score || dashboard.risk_score || dashboard.match_confidence || 0}%
                                            </span>
                                        </div>
                                        {(dashboard.risk_level || dashboard.overall_status) && (
                                            <div className="mt-4">
                                                <span className={`px-3 py-1 text-xs font-bold rounded-full uppercase ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                                                    {dashboard.risk_level || dashboard.overall_status}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Issues Distribution */}
                                    <div className={`p-5 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                                        <h4 className={`text-sm font-semibold mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Issue Distribution</h4>
                                        <div className="space-y-4">
                                            {/* Critical */}
                                            <div>
                                                <div className="flex justify-between text-xs mb-1">
                                                    <span className={`font-semibold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>Critical</span>
                                                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{dashboard.statistics?.critical_issues || dashboard.statistics?.critical_findings || 0}</span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-1.5 dark:bg-gray-700">
                                                    <div className="bg-red-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, (dashboard.statistics?.critical_issues || dashboard.statistics?.critical_findings || 0) * 10)}%` }}></div>
                                                </div>
                                            </div>
                                            {/* High */}
                                            <div>
                                                <div className="flex justify-between text-xs mb-1">
                                                    <span className={`font-semibold ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>High</span>
                                                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{dashboard.statistics?.high_issues || dashboard.statistics?.high_findings || 0}</span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-1.5 dark:bg-gray-700">
                                                    <div className="bg-orange-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, (dashboard.statistics?.high_issues || dashboard.statistics?.high_findings || 0) * 10)}%` }}></div>
                                                </div>
                                            </div>
                                            {/* Medium */}
                                            <div>
                                                <div className="flex justify-between text-xs mb-1">
                                                    <span className={`font-semibold ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>Medium</span>
                                                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{dashboard.statistics?.medium_issues || dashboard.statistics?.medium_findings || 0}</span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-1.5 dark:bg-gray-700">
                                                    <div className="bg-yellow-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, (dashboard.statistics?.medium_issues || dashboard.statistics?.medium_findings || 0) * 10)}%` }}></div>
                                                </div>
                                            </div>
                                            {/* Low */}
                                            <div>
                                                <div className="flex justify-between text-xs mb-1">
                                                    <span className={`font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>Low</span>
                                                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{dashboard.statistics?.low_issues || dashboard.statistics?.low_findings || 0}</span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-1.5 dark:bg-gray-700">
                                                    <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, (dashboard.statistics?.low_issues || dashboard.statistics?.low_findings || 0) * 10)}%` }}></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* TAB: FINDINGS */}
                {activeTab === 'findings' && (
                    <div className="space-y-4">
                        {issues.length === 0 ? (
                            <p className={`text-center py-8 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tidak ada findings</p>
                        ) : (
                            issues.map((issue: any, idx: number) => (
                                <div key={idx} className={`p-4 rounded-xl border shadow-sm ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-2">
                                                <span className={`px-2.5 py-0.5 text-[10px] font-bold uppercase rounded-md ${getSeverityColor(issue.severity)}`}>
                                                    {issue.severity || 'N/A'}
                                                </span>
                                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-md ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                                    {issue.category || issue.issue_type || issue.dimension || 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                        <span className={`text-xs font-mono ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>#{issue.issue_id || idx + 1}</span>
                                    </div>
                                    
                                    <p className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                        {issue.description || issue.explanation}
                                    </p>

                                    <div className={`mt-3 space-y-2 text-sm p-3 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                                        {issue.document_a_evidence && (
                                            <div>
                                                <span className={`text-xs font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>Doc A:</span>
                                                <p className={`text-xs mt-0.5 italic ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>"{issue.document_a_evidence}"</p>
                                            </div>
                                        )}
                                        {issue.document_b_evidence && (
                                            <div className="pt-1">
                                                <span className={`text-xs font-semibold ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>Doc B:</span>
                                                <p className={`text-xs mt-0.5 italic ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>"{issue.document_b_evidence}"</p>
                                            </div>
                                        )}
                                        {issue.original_text && (
                                            <div>
                                                <span className={`text-xs font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Teks Asli:</span>
                                                <p className={`text-xs mt-0.5 italic ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>"{issue.original_text}"</p>
                                            </div>
                                        )}
                                        {(issue.clause_reference || issue.location || issue.location_a) && (
                                            <div className="flex items-center gap-1.5 text-xs text-gray-500 pt-1">
                                                <i className="fa-solid fa-location-dot"></i>
                                                <span>{issue.clause_reference || issue.location || `A: ${issue.location_a}${issue.location_b ? `, B: ${issue.location_b}` : ''}`}</span>
                                            </div>
                                        )}
                                    </div>

                                    {(issue.recommendation || issue.suggested_revision || issue.suggested_revision_or_action) && (
                                        <div className={`mt-3 p-3 rounded-lg border-l-2 border-green-500 ${darkMode ? 'bg-green-900/10' : 'bg-green-50'}`}>
                                            <div className="flex items-center gap-1.5 mb-1 text-xs font-bold text-green-600 dark:text-green-400">
                                                <i className="fa-solid fa-lightbulb"></i>
                                                Rekomendasi
                                            </div>
                                            <p className={`text-sm ${darkMode ? 'text-green-100' : 'text-green-800'}`}>
                                                {issue.recommendation || issue.suggested_revision || issue.suggested_revision_or_action}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                        
                        {/* Missing Elements & Inconsistencies (Agent 1 Specific) */}
                        {result.missing_elements && result.missing_elements.length > 0 && (
                            <div className="mt-6">
                                <h4 className={`text-sm font-bold mb-3 flex items-center ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                                    <i className="fa-solid fa-circle-minus mr-2"></i> Missing Elements
                                </h4>
                                <div className="space-y-3">
                                    {result.missing_elements.map((el, i) => (
                                        <div key={i} className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                                            <p className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{el.element}</p>
                                            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Expected: {el.expected_based_on_submission}</p>
                                            <p className="text-xs mt-1 text-red-500">Impact: {el.impact_if_missing}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {result.inconsistencies && result.inconsistencies.length > 0 && (
                            <div className="mt-6">
                                <h4 className={`text-sm font-bold mb-3 flex items-center ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                    <i className="fa-solid fa-code-compare mr-2"></i> Inconsistencies
                                </h4>
                                <div className="space-y-3">
                                    {result.inconsistencies.map((inc, i) => (
                                        <div key={i} className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className={`px-2 py-0.5 text-xs font-bold rounded bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300`}>{inc.source}</span>
                                                <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{inc.reference}</span>
                                            </div>
                                            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{inc.description}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AICompareModal;
